import type { LookupResponse } from "@sirdab/returns-shared";
import { Alert, Textarea } from "@sirdab/ui";

import { Field, PhotoUploader, StepActions } from "../components";
import { useFlow } from "../flow";
import { useMessages } from "../i18n";
import { OfflineNote, RequireLookup } from "./shared";
import { StepScaffold } from "./StepScaffold";

/**
 * Step 4 of docs/08 — photos and notes, gated by the chosen reason's `photoRequired`
 * (policy check 7).
 *
 * Required means blocking: continue is disabled until every line whose reason demands a
 * photo has one. Optional means offered and visibly de-emphasised. The note is always
 * available and always optional.
 *
 * Only `uploadId`s are carried forward; the bytes go straight to the upload endpoint and
 * are never held in the draft or in browser storage.
 */
function Body({ lookup }: { lookup: LookupResponse }): React.JSX.Element {
  const { t, text } = useMessages();
  const {
    merchantSlug,
    shopperToken,
    lines: drafts,
    update,
    next,
    back,
  } = useFlow();

  const photoRequiredFor = (reasonCode: string | null): boolean =>
    lookup.policy.reasons.find((reason) => reason.code === reasonCode)
      ?.photoRequired === true;

  const anyRequired = drafts.some((draft) =>
    photoRequiredFor(draft.reasonCode),
  );
  const blocked = drafts.some(
    (draft) =>
      photoRequiredFor(draft.reasonCode) && draft.uploadIds.length === 0,
  );

  function patch(
    lineRef: string,
    changes: { uploadIds?: string[]; note?: string },
  ): void {
    update({
      lines: drafts.map((draft) =>
        draft.lineRef === lineRef ? { ...draft, ...changes } : draft,
      ),
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <Alert variant={anyRequired ? "warning" : "default"}>
        {anyRequired
          ? t("portal.photo_required_hint")
          : t("evidence.optional_note")}
      </Alert>
      <OfflineNote />

      {drafts.map((draft) => {
        const line = lookup.order.lines.find(
          (entry) => entry.lineRef === draft.lineRef,
        );
        const required = photoRequiredFor(draft.reasonCode);
        return (
          <section
            key={draft.lineRef}
            data-line={draft.lineRef}
            data-photo-required={required ? "true" : "false"}
            className="border-border flex flex-col gap-3 border-b pb-4 last:border-b-0"
          >
            <p className="text-label text-foreground">
              {line === undefined ? draft.lineRef : text(line.productName)}
            </p>

            <PhotoUploader
              merchantSlug={merchantSlug}
              // Empty rather than absent when the wizard has no session: the upload then
              // fails on the server and says so, which is the honest outcome. Nothing in
              // this step can mint a session of its own.
              shopperToken={shopperToken ?? ""}
              uploadIds={draft.uploadIds}
              onChange={(uploadIds) => patch(draft.lineRef, { uploadIds })}
              label={`${t("evidence.photos_label")}${required ? "" : ` (${t("ui.optional")})`}`}
              required={required}
            />

            <Field label={t("field.note.label")} hint={t("field.note.hint")}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={2}
                  value={draft.note ?? ""}
                  onChange={(event) =>
                    patch(draft.lineRef, { note: event.target.value })
                  }
                />
              )}
            </Field>
          </section>
        );
      })}

      {blocked ? (
        <p role="alert" className="text-caption text-destructive">
          {t("validation.photo_required")}
        </p>
      ) : null}

      <StepActions onNext={next} onBack={back} nextDisabled={blocked} />
    </div>
  );
}

export function EvidenceScreen(): React.JSX.Element {
  return (
    <StepScaffold
      step="evidence"
      titleKey="screen.evidence.title"
      descriptionKey="screen.evidence.description"
    >
      <RequireLookup>{(lookup) => <Body lookup={lookup} />}</RequireLookup>
    </StepScaffold>
  );
}
